import { useCallback, useEffect, useState } from "react";
import { Plus, Calculator, CheckCircle, Warning, Trash, CurrencyDollar, Download } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError, formatCurrency } from "../lib/utils-extra";

const METHOD_LABEL = {
  fair_share: "Fair Share",
  tier: "Tier-Based",
  bell_curve: "Bell Curve",
  hybrid: "Team + Individual Hybrid",
};
const METHOD_DESC = {
  fair_share: "Everyone qualified gets a proportional share based on KPI score. Best for collaborative teams.",
  tier: "Define performance tiers and the % of pool each tier receives.",
  bell_curve: "Top 10% get the largest share, bottom 20% reduced. ⚠ May reduce teamwork.",
  hybrid: "Split the pool between team-share (equal) and individual KPI-based share.",
};

const empty = () => ({
  name: "", period_start: "", period_end: "",
  pool_amount: 0, currency: "USD", departments: [],
  eligibility_mode: "all", selected_employee_ids: [],
  method: "fair_share", min_kpi_score: 60, min_attendance: 0,
  min_tenure_months: 0, require_finalized_eval: true,
  tiers: [], team_share_percent: 50, status: "draft", notes: "",
});

export default function Bonus() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty());
  const [selected, setSelected] = useState(null);
  const [settings, setSettings] = useState({ currencies: ["USD","CAD"] });
  const [employees, setEmployees] = useState([]);

  const reload = useCallback(() => {
    return api.get("/bonus-cycles").then(({ data }) => {
      setList(data);
      setSelected((cur) => (cur ? data.find((c) => c.id === cur.id) || cur : cur));
    });
  }, []);

  const loadMeta = useCallback(() => {
    Promise.all([api.get("/settings"), api.get("/employees")])
      .then(([s, e]) => { setSettings(s.data); setEmployees(e.data || []); })
      .catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    reload();
    loadMeta();
  }, [reload, loadMeta]);

  const openCreate = () => { setEditId(null); setForm(empty()); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ ...empty(), ...c }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      payload.pool_amount = Number(payload.pool_amount);
      if (editId) await api.put(`/bonus-cycles/${editId}`, payload);
      else await api.post("/bonus-cycles", payload);
      toast.success("Saved");
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const calc = async (id) => {
    try {
      const { data } = await api.post(`/bonus-cycles/${id}/calculate`);
      setSelected(data);
      toast.success("Calculated");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const finalize = async (id) => {
    if (!confirm("Finalize this bonus cycle?")) return;
    try {
      await api.post(`/bonus-cycles/${id}/finalize`);
      toast.success("Finalized");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this bonus cycle?")) return;
    await api.delete(`/bonus-cycles/${id}`);
    reload();
    if (selected?.id === id) setSelected(null);
  };

  const downloadCsv = async (id, name) => {
    try {
      const res = await api.get(`/bonus-cycles/${id}/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bonus_${(name || id).replace(/[^a-z0-9_-]/gi, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleEmployee = (id) => {
    const cur = form.selected_employee_ids || [];
    setForm({
      ...form,
      selected_employee_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  const addTier = () => setForm({ ...form, tiers: [...(form.tiers || []), { name: "", min_score: 0, max_score: 100, pool_percent: 0 }] });
  const updTier = (i, p) => {
    const t = [...form.tiers]; t[i] = { ...t[i], ...p }; setForm({ ...form, tiers: t });
  };
  const rmTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6" data-testid="bonus-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Compensation</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Bonus Distribution</h1>
          <p className="text-[#475569] text-sm mt-1">Reward fairly, transparently.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="new-bonus-button">
          <Plus size={18} className="mr-1" /> New Bonus Cycle
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3 lg:col-span-1">
          {list.map((c) => (
            <Card key={c.id} onClick={() => setSelected(c)}
                  className={`surface-card border-[#E2E8F0] cursor-pointer ${selected?.id === c.id ? "ring-2 ring-[#0EA5E9]" : ""}`}
                  data-testid={`bonus-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-base font-semibold">{c.name}</div>
                    <div className="text-xs text-[#475569]">{METHOD_LABEL[c.method]}</div>
                  </div>
                  <Badge className={
                    c.status === "finalized" ? "bg-[#16A34A]/10 text-[#16A34A] border-0" :
                    c.status === "calculated" ? "bg-[#0EA5E9]/10 text-[#0EA5E9] border-0" :
                    "bg-[#F1F5F9] text-[#475569] border-0"
                  }>{c.status}</Badge>
                </div>
                <div className="mt-3 font-display text-xl font-semibold">{formatCurrency(c.pool_amount, c.currency)}</div>
                <div className="text-xs text-[#475569] mt-1">{c.period_start} → {c.period_end}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <Card className="surface-card border-[#E2E8F0]" data-testid="bonus-detail">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="small-label">{METHOD_LABEL[selected.method]}</div>
                    <div className="font-display text-2xl font-semibold mt-1">{selected.name}</div>
                    <div className="text-sm text-[#475569]">{selected.period_start} → {selected.period_end} · Pool {formatCurrency(selected.pool_amount, selected.currency)}</div>
                    {selected.eligibility_mode === "individual" && (
                      <Badge className="mt-2 bg-[#7C3AED]/10 text-[#7C3AED] border-0" data-testid="bonus-individual-badge">
                        Individual selection · {(selected.selected_employee_ids || []).length} included
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(selected)}>Edit</Button>
                    <Button onClick={() => calc(selected.id)} className="bg-[#0EA5E9] hover:bg-[#0284C7]" size="sm" data-testid="calculate-bonus-button">
                      <Calculator size={14} className="mr-1" /> Calculate
                    </Button>
                    {selected.status === "calculated" && (
                      <Button onClick={() => finalize(selected.id)} size="sm" className="bg-[#16A34A] hover:bg-[#15803D]" data-testid="finalize-bonus-button">
                        <CheckCircle size={14} className="mr-1" /> Finalize
                      </Button>
                    )}
                    {selected.results?.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => downloadCsv(selected.id, selected.name)}
                              data-testid="bonus-csv-button">
                        <Download size={14} className="mr-1" /> CSV
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => remove(selected.id)}>
                      <Trash size={14} className="text-[#DC2626]" />
                    </Button>
                  </div>
                </div>

                {selected.method === "bell_curve" && (
                  <div className="text-xs flex items-center gap-2 px-3 py-2 bg-[#D97706]/10 text-[#D97706] rounded-lg">
                    <Warning size={14} weight="duotone" /> Bell curve distribution may reduce teamwork in collaborative environments.
                  </div>
                )}

                {selected.results?.length === 0 ? (
                  <div className="text-sm text-[#475569] py-8 text-center">Click <strong>Calculate</strong> to compute bonus distribution.</div>
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-[#475569] uppercase tracking-wider">
                          <th className="px-3 py-2">Rank</th>
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2">Department</th>
                          <th className="px-3 py-2 text-right">KPI</th>
                          <th className="px-3 py-2">Eligible</th>
                          <th className="px-3 py-2 text-right">Bonus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.results?.map((r) => (
                          <tr key={r.employee_id} className="border-t border-[#E2E8F0]" data-testid={`bonus-result-${r.employee_id}`}>
                            <td className="px-3 py-2 font-mono-data">{r.rank}</td>
                            <td className="px-3 py-2 font-medium">{r.name}</td>
                            <td className="px-3 py-2 text-[#475569]">{r.department}</td>
                            <td className="px-3 py-2 text-right font-mono-data">{r.kpi_score?.toFixed(1) || "—"}</td>
                            <td className="px-3 py-2">
                              {r.eligible
                                ? <Badge className="bg-[#16A34A]/10 text-[#16A34A] border-0">Yes</Badge>
                                : <Badge className="bg-[#DC2626]/10 text-[#DC2626] border-0" title={r.reason}>No</Badge>}
                              {!r.eligible && <div className="text-[10px] text-[#475569] mt-1">{r.reason}</div>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-data font-semibold">
                              {r.eligible ? formatCurrency(r.bonus_amount, selected.currency) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-10 text-center">
              <CurrencyDollar size={32} className="mx-auto text-[#475569]" weight="duotone" />
              <div className="mt-3 text-sm text-[#475569]">Pick a bonus cycle to view results.</div>
            </CardContent></Card>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit bonus cycle" : "New bonus cycle"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="basics">
            <TabsList className="bg-[#F1F5F9]">
              <TabsTrigger value="basics">Basics</TabsTrigger>
              <TabsTrigger value="rules">Eligibility</TabsTrigger>
              <TabsTrigger value="method">Method</TabsTrigger>
            </TabsList>
            <TabsContent value="basics" className="space-y-4 mt-4">
              <div>
                <Label className="small-label">Cycle Name</Label>
                <Input className="mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bonus-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-label">Period Start</Label>
                  <Input type="date" className="mt-2" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
                </div>
                <div>
                  <Label className="small-label">Period End</Label>
                  <Input type="date" className="mt-2" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-label">Pool Amount</Label>
                  <Input type="number" className="mt-2" value={form.pool_amount} onChange={(e) => setForm({ ...form, pool_amount: e.target.value })} data-testid="bonus-pool-input" />
                </div>
                <div>
                  <Label className="small-label">Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(settings.currencies || ["USD","CAD"]).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="small-label">Notes</Label>
                <Textarea rows={2} className="mt-2" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </TabsContent>

            <TabsContent value="rules" className="space-y-4 mt-4">
              <div>
                <Label className="small-label">Eligibility Mode</Label>
                <Select
                  value={form.eligibility_mode || "all"}
                  onValueChange={(v) => setForm({ ...form, eligibility_mode: v })}
                >
                  <SelectTrigger className="mt-2" data-testid="bonus-eligibility-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All eligible employees (by criteria)</SelectItem>
                    <SelectItem value="individual">Individual selection</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#475569] mt-2">
                  {form.eligibility_mode === "individual"
                    ? "Only the employees you select below are included. Non-selected staff are excluded even if they meet score/department criteria. KPI score thresholds still apply to the selected employees."
                    : "Every active employee is assessed against the score/department/tenure criteria below."}
                </p>
              </div>

              {form.eligibility_mode === "individual" && (
                <div data-testid="bonus-employee-picker">
                  <div className="flex items-center justify-between">
                    <Label className="small-label">Select Employees ({(form.selected_employee_ids || []).length} selected)</Label>
                    <button
                      type="button"
                      className="text-xs text-[#0EA5E9] hover:underline"
                      onClick={() => setForm({
                        ...form,
                        selected_employee_ids: (form.selected_employee_ids || []).length === employees.length
                          ? [] : employees.map((e) => e.id),
                      })}
                      data-testid="bonus-select-all-toggle"
                    >
                      {(form.selected_employee_ids || []).length === employees.length && employees.length > 0 ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-[#E2E8F0] divide-y divide-[#F1F5F9]">
                    {employees.length === 0 && (
                      <div className="p-3 text-sm text-[#475569]">No employees found.</div>
                    )}
                    {employees.map((e) => {
                      const checked = (form.selected_employee_ids || []).includes(e.id);
                      return (
                        <label
                          key={e.id}
                          className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-[#F8FAFC]"
                          data-testid={`bonus-employee-option-${e.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEmployee(e.id)}
                            data-testid={`bonus-employee-checkbox-${e.id}`}
                          />
                          <span className="text-sm">
                            {e.full_name}
                            {(e.position_name || e.department) && (
                              <span className="text-xs text-[#475569]"> · {e.position_name || e.department}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-label">Min KPI Score</Label>
                  <Input type="number" className="mt-2" value={form.min_kpi_score} onChange={(e) => setForm({ ...form, min_kpi_score: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="small-label">Min Attendance %</Label>
                  <Input type="number" className="mt-2" value={form.min_attendance} onChange={(e) => setForm({ ...form, min_attendance: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="small-label">Min Tenure (months)</Label>
                  <Input type="number" className="mt-2" value={form.min_tenure_months} onChange={(e) => setForm({ ...form, min_tenure_months: Number(e.target.value) })} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-[#F1F5F9] mt-6">
                  <Label className="text-sm">Require Finalized Eval</Label>
                  <input type="checkbox" checked={form.require_finalized_eval} onChange={(e) => setForm({ ...form, require_finalized_eval: e.target.checked })} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="method" className="space-y-4 mt-4">
              <div>
                <Label className="small-label">Distribution Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="mt-2" data-testid="bonus-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(METHOD_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#475569] mt-2">{METHOD_DESC[form.method]}</p>
              </div>

              {form.method === "tier" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="small-label">Tiers</Label>
                    <Button size="sm" variant="outline" onClick={addTier}>Add tier</Button>
                  </div>
                  {form.tiers.map((t, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 items-end">
                      <Input placeholder="Name" value={t.name} onChange={(e) => updTier(i, { name: e.target.value })} />
                      <Input type="number" placeholder="Min" value={t.min_score} onChange={(e) => updTier(i, { min_score: Number(e.target.value) })} />
                      <Input type="number" placeholder="Max" value={t.max_score} onChange={(e) => updTier(i, { max_score: Number(e.target.value) })} />
                      <Input type="number" placeholder="% of pool" value={t.pool_percent} onChange={(e) => updTier(i, { pool_percent: Number(e.target.value) })} />
                      <Button variant="ghost" size="sm" onClick={() => rmTier(i)}><Trash size={14} className="text-[#DC2626]" /></Button>
                    </div>
                  ))}
                </div>
              )}

              {form.method === "hybrid" && (
                <div>
                  <Label className="small-label">Team Share % (rest goes to individual)</Label>
                  <Input type="number" className="mt-2" value={form.team_share_percent}
                         onChange={(e) => setForm({ ...form, team_share_percent: Number(e.target.value) })} />
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="save-bonus-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
